import { USER_ROLES } from "@/constants";
import { PHISH_SERVICE, SEG_SERVICE } from "@/utils/services";
import { useFlag } from "@/composables/useFlags/useFlag";

const { isFeatureAvailable: isQuarantineConfigurationAvailable } = useFlag(
  "QUARANTINE_CONFIGURATION",
);

const { isFeatureAvailable: isGeoblockingAvailable } = useFlag(
  "GEOBLOCKING_CONFIG_PAGE",
);

const { isFeatureAvailable: isPatternFilteringAvailable } =
  useFlag("PATTERN_FILTERING");

const { isFeatureAvailable: isBlockListAvailable } = useFlag(
  "BLOCK_LIST_MAIL_FLOW_LINK",
);

const redirectLegacyMailFlowTab = (_to, _from, next) => {
  next({ name: "customer-configuration-mail-flow-settings" });
};

const children = [
  {
    path: "overview",
    name: "customer-email_security-overview",
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Overview/index.vue"),
    exact: true,
    meta: {
      authorize: [USER_ROLES.SYSTEM, USER_ROLES.SALES, USER_ROLES.MSP],
    },
  },
  {
    path: "reports",
    name: "customer-email_security-reports",
    exact: true,
    redirect: {
      name: "summaryCustomerReport",
    },
    children: [
      {
        path: "summary",
        name: "summaryCustomerReport",
        component: () =>
          import("@/modules/PhishTitan/views/Common/Reports/Summary.vue"),
      },
      {
        path: "insights",
        name: "insightsCustomerReport",
        component: () =>
          import("@/modules/PhishTitan/views/Common/Reports/Insights.vue"),
      },
    ],
  },
  {
    path: "notifications",
    name: "customer-email_security-notifications",
    component: () =>
      import("@/modules/PhishTitan/views/Common/Notifications/Notifications.vue"),
  },
  {
    path: "configuration",
    name: "customer-email_security-configuration",
    exact: true,
    redirect: { name: "customer-configuration-mail-flow" },
    children: [
      {
        path: "mail_flow",
        name: "customer-configuration-mail-flow",
        component: () =>
          import("@/modules/PhishTitan/views/Customer/Configuration/MailFlow/MailFlow.vue"),
        exact: true,
        redirect: { name: "customer-configuration-mail-flow-settings" },
        children: [
          {
            path: "settings",
            name: "customer-configuration-mail-flow-settings",
            component: () =>
              import("@/modules/PhishTitan/views/Common/mail-flow-settings/mail-flow-settings.index.vue"),
          },
          {
            path: "remediation",
            name: "customer-configuration-mail-flow-remediation",
            component: () =>
              import("@/modules/PhishTitan/views/Common/Remediation/index.vue"),
            beforeEnter: redirectLegacyMailFlowTab,
          },
          {
            path: "exploited_domains",
            name: "customer-configuration-mail-flow-exploited-domains",
            component: () =>
              import("@/modules/PhishTitan/views/Common/ExploitedDomains/ExploitedDomains.vue"),
            beforeEnter: redirectLegacyMailFlowTab,
          },
          {
            path: "anti_spoofing",
            name: "customer-configuration-mail-flow-anti-spoofing",
            component: () =>
              import("@/modules/PhishTitan/views/Common/AntiSpoofing/AntiSpoofing.vue"),
            beforeEnter: redirectLegacyMailFlowTab,
          },
          {
            path: "graymail",
            name: "customer-configuration-mail-flow-graymail",
            component: () =>
              import("@/modules/PhishTitan/views/Common/Graymail/Graymail.vue"),
            beforeEnter: redirectLegacyMailFlowTab,
          },
          {
            path: "frontline",
            name: "customer-configuration-mail-flow-frontline",
            meta: {
              solutionId: SEG_SERVICE,
            },
            component: () =>
              import("@/modules/PhishTitan/views/Common/Frontline/Frontline.vue"),
          },
        ],
      },
      {
        path: "link_lock",
        name: "customer-email_security-config-link-lock",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/LinkLock/LinkLock.vue"),
      },
      {
        path: "exemptions",
        name: "customer-email_security-config-exemptions",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/Exemptions/Exemptions.vue"),
      },
      {
        path: "pattern_filtering",
        name: "customer-email_security-config-pattern-filtering",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/PatternFiltering/PatternFiltering.vue"),
        beforeEnter: (_to, _from, next) => {
          if (!isPatternFilteringAvailable.value) {
            next({ name: "customer-configuration-mail-flow" });
            return;
          }
          next();
        },
      },
      {
        path: "email_digest",
        name: "customer-email_security-config-email-digest",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/quarantine-configuration/quarantine-configuration.index.vue"),
        meta: {
          solutionId: SEG_SERVICE,
        },
        beforeEnter: (_to, _from, next) => {
          if (!isQuarantineConfigurationAvailable.value) {
            next({ name: "customer-configuration-mail-flow" });
            return;
          }
          next();
        },
      },
      {
        path: "block_list",
        name: "customer-email_security-config-block-list",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/BlockList/BlockList.vue"),
        beforeEnter: (_to, _from, next) => {
          if (!isBlockListAvailable.value) {
            next({ name: "customer-configuration-mail-flow" });
            return;
          }
          next();
        },
      },
      {
        path: "geoblocking",
        name: "customer-email_security-config-geoblocking",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/Geoblocking/Geoblocking.vue"),
        beforeEnter: (_to, _from, next) => {
          if (!isGeoblockingAvailable.value) {
            next({ name: "customer-configuration-mail-flow" });
            return;
          }
          next();
        },
      },
    ],
  },
  {
    path: "incidents",
    name: "customer-email_security-incidents",
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Incidents/Overview/index.vue"),
  },
  {
    path: "incidents/incident_summary/:ingressId",
    name: "customer-email_security-incidents-incident-summary",
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/IncidentSummary.vue"),
    redirect: {
      name: "customer-email_security-incidents-incident-summary-details",
    },
    children: [
      {
        path: "details",
        name: "customer-email_security-incidents-incident-summary-details",
        component: () =>
          import("@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/Details/index.vue"),
      },
      {
        path: "threat-coach",
        name: "customer-email_security-incidents-incident-summary-threat-coach",
        component: () =>
          import("@/modules/PhishTitan/views/Common/Incidents/IncidentSummary/ThreatCoach/ThreatCoach"),
      },
      {
        path: "affected-users",
        name: "customer-email_security-incidents-incident-summary-affected-users",
        component: () =>
          import("@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/AffectedUsers/index.vue"),
      },
      {
        path: "received-headers",
        name: "customer-email_security-incidents-incident-summary-received-headers",
        component: () =>
          import("@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/ReceivedHeaders/index.vue"),
      },
      {
        path: "timeline",
        name: "customer-email_security-incidents-incident-summary-incident-timeline",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
      {
        path: "audit-logs",
        name: "customer-email_security-incidents-incident-summary-audit-logs",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
      {
        path: "notes",
        name: "customer-email_security-incidents-incident-summary-audit-notes",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
    ],
  },
  {
    path: "domains",
    name: "customer-email_security-domains",
    meta: {
      solutionId: SEG_SERVICE,
    },
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Domains/Domains.vue"),
  },
  {
    path: "email-history",
    name: "customer-email_security-email-history",
    component: () =>
      import("@/modules/PhishTitan/views/Common/EmailHistory/EmailHistory.vue"),
  },
  {
    path: "quarantine",
    name: "customer-email_security-quarantine",
    meta: {
      solutionId: SEG_SERVICE,
    },
    component: () =>
      import("@/modules/PhishTitan/views/Common/Quarantine/Quarantine.vue"),
  },
];

export const customerEmailSecurityRoutes = [
  {
    path: "email_security",
    name: "customer-email_security",
    component: () => import("@/modules/PhishTitan/views/Customer"),
    meta: {
      solutionId: PHISH_SERVICE,
    },
    redirect: {
      name: "customer-email_security-overview",
    },
    children,
  },
];

export const customerRoutes = {
  path: "",
  component: () => import("@/layouts/Customer/CustomerLayout.vue"),
  children: customerEmailSecurityRoutes,
};
